import React from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Check, Crown, Database, PenTool, BarChart, Contact, X } from 'lucide-react';

const PremiumPromotionModal = ({ open, onOpenChange }) => {
    const { t } = useTranslation();

    const features = [
        {
            icon: Database,
            title: t('analytics.premiumPromotion.featureStorage'),
            desc: t('analytics.premiumPromotion.featureStorageDesc'),
            color: 'text-blue-500',
            bg: 'bg-blue-500/10'
        },
        {
            icon: PenTool,
            title: t('analytics.premiumPromotion.featureDesigner'),
            desc: t('analytics.premiumPromotion.featureDesignerDesc'),
            color: 'text-purple-500',
            bg: 'bg-purple-500/10'
        },
        {
            icon: BarChart,
            title: t('analytics.premiumPromotion.featureStats'),
            desc: t('analytics.premiumPromotion.featureStatsDesc'),
            color: 'text-amber-500',
            bg: 'bg-amber-500/10'
        },
        {
            icon: Contact,
            title: t('analytics.premiumPromotion.featureSupport'),
            desc: t('analytics.premiumPromotion.featureSupportDesc'),
            color: 'text-green-500',
            bg: 'bg-green-500/10'
        }
    ];

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="bg-[#0a0a0b]/95 backdrop-blur-xl border-gray-800 text-white max-w-2xl p-0 overflow-hidden shadow-2xl">
                <div className="relative">
                    {/* Header Background Decoration */}
                    <div className="absolute inset-0 bg-gradient-to-br from-blue-600/20 via-purple-600/20 to-transparent -z-10" />
                    <div className="p-8 text-center pt-12">
                        <div className="w-20 h-20 rounded-2xl bg-gradient-to-tr from-amber-400 to-amber-600 flex items-center justify-center mx-auto mb-6 shadow-[0_0_30px_rgba(251,191,36,0.3)] animate-pulse">
                            <Crown className="w-10 h-10 text-white" />
                        </div>
                        <h2 className="text-3xl font-bold mb-2 tracking-tight">
                            {t('analytics.premiumPromotion.title')}
                        </h2>
                        <p className="text-gray-400 text-lg">
                            {t('analytics.premiumPromotion.subtitle')}
                        </p>
                    </div>

                    <div className="px-8 pb-8">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {features.map((feature, index) => {
                                const Icon = feature.icon;
                                return (
                                    <div
                                        key={index}
                                        className="p-5 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 transition-colors group"
                                    >
                                        <div className={`p-3 rounded-lg ${feature.bg} ${feature.color} w-fit mb-4 group-hover:scale-110 transition-transform`}>
                                            <Icon className="w-6 h-6" />
                                        </div>
                                        <h3 className="font-bold text-white mb-2">{feature.title}</h3>
                                        <p className="text-sm text-gray-400 leading-relaxed">
                                            {feature.desc}
                                        </p>
                                    </div>
                                );
                            })}
                        </div>

                        <div className="mt-8 p-6 rounded-2xl bg-gradient-to-r from-blue-600/10 to-purple-600/10 border border-blue-500/20 flex flex-col items-center justify-center text-center">
                            <p className="text-white font-medium mb-4">
                                {t('analytics.premiumPromotion.contactForUpgrade')}
                            </p>
                            <div className="flex gap-4 w-full">
                                <Button
                                    className="flex-1 bg-white text-black hover:bg-gray-200 font-bold"
                                    onClick={() => window.open('mailto:info@snapp.ai')}
                                >
                                    info@snapp.ai
                                </Button>
                                <Button
                                    className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-bold"
                                    onClick={() => onOpenChange(false)}
                                >
                                    {t('analytics.premiumPromotion.close')}
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default PremiumPromotionModal;
